import { z } from 'zod';
import { UuidSchema, IsoDateTimeSchema } from '../common/ids.js';
import { PaginationQuerySchema } from '../common/pagination.js';
import { DiscoveryQuerySchema } from '../common/discovery.js';
/** `tasks.status` — `cancelled` is a supervisor withdrawal, never a worker action (T-06). */
export const TaskStatusSchema = z.enum(['pending', 'done', 'unable', 'cancelled']);
/** `task_photos.status` — `pending` is presigned but unconfirmed, and sweepable after 24h. */
export const TaskPhotoStatusSchema = z.enum(['pending', 'attached']);
/**
 * A photo attached to a task. Deliberately **not** the whole `task_photos`
 * row: `objectKey` is the bucket path and `uploadedById` repeats the task's
 * own `completedById`, and neither answers anything a client asks. The bytes
 * are fetched through `GET /tasks/:id/photos/:photoId`, which redirects to a
 * short-lived presigned GET (T-11) — there is no public URL on this shape.
 */
export const TaskPhotoSchema = z.object({
    id: UuidSchema,
    status: TaskPhotoStatusSchema,
    contentType: z.string().min(1),
    sizeBytes: z.int().min(0),
    createdAt: IsoDateTimeSchema,
});
/**
 * A written instruction for one shift (Phase 5, T-01).
 *
 * `shiftId` `null` is the unassigned pool, which is why `contractId`/`siteId`
 * are on the task itself rather than borrowed from the shift (T-02): a task
 * with no shift is still scoped, filterable and isolatable. The composite FK
 * makes `task.contract/site = shift.contract/site` a database guarantee once
 * a shift is set (T-03).
 */
export const TaskSchema = z.object({
    id: UuidSchema,
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.nullable(),
    /** `null` = the unassigned pool (T-01). */
    shiftId: UuidSchema.nullable(),
    /** `null` exactly when `shiftId` is. */
    assignedAt: IsoDateTimeSchema.nullable(),
    title: z.string().min(1),
    instructions: z.string().min(1).nullable(),
    blocksPunchOut: z.boolean(),
    photoRequired: z.boolean(),
    status: TaskStatusSchema,
    /** The worker's statement, ≥ 10 chars, mandatory on `done` and `unable`
     * (T-06) — and on `cancelled`, where it is the supervisor's reason. */
    justification: z.string().min(1).nullable(),
    completedAt: IsoDateTimeSchema.nullable(),
    completedById: UuidSchema.nullable(),
    createdById: UuidSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    photos: z.array(TaskPhotoSchema),
    // ---- read-time joins, house convention (cf. `ExceptionSchema`) ---------
    // All `.optional()`: absent means "not resolved for this response", never
    // "there is none" — that is what `null` says.
    siteName: z.string().min(1).optional(),
    zoneName: z.string().min(1).nullable().optional(),
    createdByName: z.string().min(1).optional(),
    completedByName: z.string().min(1).nullable().optional(),
});
/**
 * `GET /tasks`. `assigned` defaults to `all` rather than `unassigned`: the
 * inbox default belongs to the admin app's URL state, not to the API, or a
 * caller asking a plain question gets a filtered answer it never requested.
 */
export const TaskListQuerySchema = DiscoveryQuerySchema.extend(PaginationQuerySchema.shape).extend({
    status: TaskStatusSchema.optional(),
    assigned: z.enum(['assigned', 'unassigned', 'all']).default('all'),
    shiftId: UuidSchema.optional(),
    /** The worker the task's shift is assigned to — `tasks` has no user column. */
    assignedUserId: UuidSchema.optional(),
});
/** `POST /tasks` — `shiftId` creates straight onto a shift, else into the pool. */
export const CreateTaskRequestSchema = z.object({
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.nullable().optional(),
    title: z.string().trim().min(1).max(200),
    instructions: z.string().trim().min(1).max(4000).optional(),
    blocksPunchOut: z.boolean(),
    photoRequired: z.boolean(),
    shiftId: UuidSchema.optional(),
});
/**
 * `PATCH /tasks/:id`. No `contractId`/`siteId`: those are the scope the
 * composite FK holds, and re-parenting a row across a contract is not an edit
 * (same rule as `ShiftPatchRequestSchema`). No `shiftId` either —
 * assign/unassign are their own audited endpoints.
 */
export const UpdateTaskRequestSchema = z
    .object({
    zoneId: UuidSchema.nullable(),
    title: z.string().trim().min(1).max(200),
    instructions: z.string().trim().min(1).max(4000).nullable(),
    blocksPunchOut: z.boolean(),
    photoRequired: z.boolean(),
})
    .partial()
    .strict();
/** `POST /tasks/:id/assign`. The shift must be `scheduled` or `in_progress` (T-04). */
export const AssignTaskRequestSchema = z.object({
    shiftId: UuidSchema,
});
/** `POST /tasks/:id/cancel` — terminal and audited, so the reason is mandatory (T-16). */
export const CancelTaskRequestSchema = z.object({
    reason: z.string().trim().min(10).max(500),
});
/** `GET /tasks/assignable-shifts?userId=&taskId=` — the assignment picker (T-04). */
export const AssignableShiftsQuerySchema = z.object({
    taskId: UuidSchema,
    userId: UuidSchema.optional(),
});
//# sourceMappingURL=task.js.map